import { convertUnixTimestampToDate } from '@/src/shared/utils/datetime';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { MChatAccount } from '../../models/chat-account';
import { MChatContact } from '../../models/chat-contact';
import { MChatConversation } from '../../models/chat-conversation';
import { MChatMember } from '../../models/chat-member';
import { MChatMessage, TChatMessageType } from '../../models/chat-message';
import { MChatMessageAttachment } from '../../models/chat-message-attachment';
import { TChatAccount } from '../schemas/api/responses/chat-account';
import { TChatContact } from '../schemas/api/responses/chat-contact';
import { TChatConversation } from '../schemas/api/responses/chat-conversation';
import { TChatMember } from '../schemas/api/responses/chat-member';
import {
  TChatMessage,
  TChatMessageAttachment,
} from '../schemas/api/responses/chat-message';
import { TChatwootContact } from '../schemas/chatwoot/messages/contact';
import { TChatwootMessageAttachment } from '../schemas/chatwoot/messages/message-attachment';
import { TChatwootMessageCreatedMessage } from '../schemas/chatwoot/messages/message-created';
import {
  SSubscribeToChatwootMessage,
  TSubscribeToChatwootMessage,
} from '../schemas/chatwoot/messages/subscribe';
import {
  SSubscribeToChatwootIdentifierMessage,
  TSubscribeToChatwootIdentifierMessage,
} from '../schemas/chatwoot/messages/subscribe-identifier';

interface ICreateChatwootSubscribeMessageParams {
  pubsubToken: string;
  userId?: number;
  accountId?: number;
}

export function mapChatMessageSchemaToChatMessageModel(
  schema: TChatMessage
): MChatMessage {
  const attachments = schema.attachments?.map((attachment) => {
    return mapChatMessageAttachmentSchemaToChatMessageAttachmentModel(
      attachment
    );
  });

  return new MChatMessage({
    id: schema.id,
    type: schema.content_type as TChatMessageType,
    content: schema.content,
    sender: mapChatMemberSchemaToChatMemberModel(schema.sender),
    sentAt: schema.sent_at,
    attachments: attachments || null,
    fromMe: schema.from_me,
  });
}

export function mapChatContactSchemaToChatContactModel(
  schema: TChatContact
): MChatContact {
  return new MChatContact({
    id: schema.id,
    name: schema.name,
    pubsubToken: schema.pubsub_token,
  });
}

export function mapChatMemberSchemaToChatMemberModel(schema: TChatMember) {
  return new MChatMember({
    id: schema.id,
    name: schema.name,
    branchId: schema.extra_attributes?.branch_id || null,
  });
}

export function mapChatConversationSchemaToChatConversationModel(
  schema: TChatConversation
) {
  return new MChatConversation({
    id: schema.id,
    recipient: mapChatMemberSchemaToChatMemberModel(schema.recipient),
    unreadMessageCount: schema.unread_message_count,
    lastMessage: schema.last_message
      ? mapChatMessageSchemaToChatMessageModel(schema.last_message)
      : null,
  });
}

function mapChatMessageAttachmentSchemaToChatMessageAttachmentModel(
  schema: TChatMessageAttachment
): MChatMessageAttachment {
  return new MChatMessageAttachment({
    id: schema.id,
    fileUrl: schema.file_url,
    type: schema.type,
    fileName: schema.file_name,
    fileExtension: schema.file_extension,
    thumbnailUrl: schema.thumbnail_url,
  });
}

export function createChatwootSubscribeMessage({
  pubsubToken,
  userId,
  accountId,
}: ICreateChatwootSubscribeMessageParams) {
  const identifier = safeParseZodObject<TSubscribeToChatwootIdentifierMessage>(
    SSubscribeToChatwootIdentifierMessage,
    {
      channel: 'RoomChannel',
      pubsub_token: pubsubToken,
      ...(userId ? { user_id: userId } : {}),
      ...(accountId ? { account_id: accountId } : {}),
    }
  );
  const message = safeParseZodObject<TSubscribeToChatwootMessage>(
    SSubscribeToChatwootMessage,
    {
      command: 'subscribe',
      identifier: JSON.stringify(identifier),
    }
  );
  return JSON.stringify(message);
}

function mapChatwootMessageAttachmentToChatMessageAttachmentModel(
  attachment: TChatwootMessageAttachment
): MChatMessageAttachment {
  const getFileName = () => {
    const baseFileName = decodeURIComponent(
      attachment.data_url.split('/').pop() || 'file'
    );
    if (!baseFileName.includes('.')) {
      return baseFileName;
    }
    return baseFileName.split('.').slice(0, -1).join('.');
  };

  const getFileExtension = () => {
    const baseFileName = decodeURIComponent(
      attachment.data_url.split('/').pop() || 'file'
    );
    return baseFileName.split('.').pop() || null;
  };

  return new MChatMessageAttachment({
    id: attachment.id,
    fileUrl: attachment.data_url,
    type: attachment.file_type,
    fileName: getFileName(),
    fileExtension: getFileExtension(),
    thumbnailUrl: attachment.thumb_url,
  });
}

function mapChatwootContactToChatMemberModel(
  contact: TChatwootContact
): MChatMember {
  return new MChatMember({
    id: contact.id,
    name: contact.name,
    branchId: null,
  });
}

export function mapChatwootMessageCreatedMessageToChatMessageModel(
  message: TChatwootMessageCreatedMessage,
  currentContactOrAccountId: number
): MChatMessage {
  return new MChatMessage({
    id: message.id,
    type: message.content_type as TChatMessageType,
    content: message.content,
    sender: mapChatwootContactToChatMemberModel(message.sender),
    sentAt: convertUnixTimestampToDate(message.created_at),
    attachments:
      message.attachments?.map((attachment) =>
        mapChatwootMessageAttachmentToChatMessageAttachmentModel(attachment)
      ) || null,
    fromMe: message.sender_id === currentContactOrAccountId,
  });
}

export function mapChatAccountSchemaToChatAccountModel(
  schema: TChatAccount
): MChatAccount {
  const fullName = schema.branch.user?.first_name;
  if (schema.branch.user?.last_name) {
    fullName?.concat(` ${schema.branch.user.last_name}`);
  }

  return new MChatAccount({
    id: schema.id,
    accountId: schema.account_id,
    name: fullName ?? '',
    pubsubToken: schema.pubsub_token,
  });
}
